import React from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './AdminLayout.module.css';

export default function AdminLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = React.useState(false);

    // Close the mobile sidebar whenever the route changes
    React.useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    // Close on Escape and lock background scroll while the mobile sidebar is open
    React.useEffect(() => {
        if (!sidebarOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setSidebarOpen(false);
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [sidebarOpen]);

    // Check admin access
    React.useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate('/login', { replace: true });
        } else if (!user.role) {
            // User object is missing role field - force re-login to get updated data
            console.log('User missing role field, forcing re-login');
            logout();
            navigate('/login', { replace: true });
        } else if (!['admin', 'super_admin'].includes(user.role)) {
            navigate('/', { replace: true });
        }
    }, [user, loading, navigate, logout]);

    if (loading || !user || !user.role || !['admin', 'super_admin'].includes(user.role)) {
        return null;
    }

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className={styles.adminLayout}>
            <header className={styles.mobileTopBar}>
                <button
                    type="button"
                    className={styles.menuBtn}
                    onClick={() => setSidebarOpen(true)}
                    aria-label="Open admin menu"
                    aria-expanded={sidebarOpen}
                    aria-controls="admin-sidebar"
                >
                    ☰
                </button>
                <span className={styles.mobileTitle}>🎬 Aluvo Admin</span>
            </header>

            {sidebarOpen && (
                <div
                    className={styles.backdrop}
                    onClick={() => setSidebarOpen(false)}
                    aria-hidden="true"
                />
            )}

            <aside
                id="admin-sidebar"
                className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}
            >
                <div className={styles.sidebarHeader}>
                    <button
                        type="button"
                        className={styles.closeBtn}
                        onClick={() => setSidebarOpen(false)}
                        aria-label="Close admin menu"
                    >
                        ✕
                    </button>
                    <h2 className={styles.logo}>🎬 Aluvo Admin</h2>
                    <div className={styles.userBadge}>
                        <span className={styles.userName}>{user.username}</span>
                        <span className={styles.userRole}>{user.role}</span>
                    </div>
                </div>

                <nav className={styles.nav}>
                    <NavLink
                        to="/admin"
                        end
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        📊 Overview
                    </NavLink>
                    <NavLink
                        to="/admin/feedback"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        💬 Feedback
                    </NavLink>
                    <NavLink
                        to="/admin/quiz-reports"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🚨 Quiz Reports
                    </NavLink>
                    <NavLink
                        to="/admin/users"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        👥 Users
                    </NavLink>
                    <NavLink
                        to="/admin/onboarding"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🧭 Onboarding
                    </NavLink>
                    <NavLink
                        to="/admin/activation"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🚀 Activation
                    </NavLink>
                    <NavLink
                        to="/admin/coins"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🪙 Coins
                    </NavLink>
                    <NavLink
                        to="/admin/collections"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🗂️ Collections
                    </NavLink>
                    <NavLink
                        to="/admin/curated-lists"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🎞️ Curated Lists
                    </NavLink>
                    <NavLink
                        to="/admin/editorial-picks"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        ⭐ Aluvo Picks on Home
                    </NavLink>
                    <NavLink
                        to="/admin/avatars"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🧑‍🎤 Avatars
                    </NavLink>
                    <NavLink
                        to="/admin/challenge-loaders"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        ⏳ Challenge Loaders
                    </NavLink>
                    <NavLink
                        to="/admin/campaigns"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        ✉️ Campaigns
                    </NavLink>
                    <NavLink
                        to="/admin/emails"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        📬 Emails
                    </NavLink>
                    <NavLink
                        to="/admin/quiz-questions"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        ❓ Quiz Questions
                    </NavLink>
                    <NavLink
                        to="/admin/quiz-pools"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🗂️ Quiz Pools
                    </NavLink>
                    <NavLink
                        to="/admin/user-reports"
                        className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
                    >
                        🚩 User Reports
                    </NavLink>
                </nav>

                <div className={styles.sidebarFooter}>
                    <button onClick={() => navigate('/')} className={styles.footerBtn}>
                        ← Back to App
                    </button>
                    <button onClick={handleLogout} className={styles.footerBtn}>
                        Logout
                    </button>
                </div>
            </aside>

            <main className={styles.content}>
                <Outlet />
            </main>
        </div>
    );
}
